import axios from 'axios'
import { logWorldCupDebug } from '@/utils/worldcupDebug'

const REQUEST_TIMEOUT = 20000
const nativeQueue = []
let nativeActive = null
let nativeCallbackInstalled = false
let previousResponseApi = null

const service = axios.create({
  baseURL: process.env.VUE_APP_WORLDCUP_URL || process.env.VUE_APP_SHORT_URL,
  timeout: REQUEST_TIMEOUT
})

function appendTimestamp (config) {
  if (String(config.method || 'get').toLowerCase() !== 'get') {
    return config
  }
  return Object.assign({}, config, {
    params: Object.assign({}, config.params, { _t: Date.now() })
  })
}

function isIOSWebView () {
  if (typeof window === 'undefined' || !window.navigator) {
    return false
  }
  const ua = window.navigator.userAgent || ''
  return /iPhone|iPad|iPod/i.test(ua)
}

function hasNativeRequestApi () {
  return !!(
    typeof window !== 'undefined' &&
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.requestApi &&
    typeof window.webkit.messageHandlers.requestApi.postMessage === 'function'
  )
}

function shouldUseNativeApi () {
  return isIOSWebView() && hasNativeRequestApi()
}

function buildNativeUrl (url) {
  if (/^https?:\/\//i.test(url)) {
    return url
  }
  const baseURL = (process.env.VUE_APP_SHORT_URL || '').replace(/\/$/, '')
  return baseURL + url
}

function shouldUseSafeNative (config) {
  return shouldUseNativeApi() && config && config.url === '/worldcup2026/newsinfo'
}

function readResultLength (data) {
  const result = data && data.result
  return Array.isArray(result && result.data) ? result.data.length : undefined
}

function installNativeCallback () {
  if (nativeCallbackInstalled || typeof window === 'undefined') {
    return
  }

  nativeCallbackInstalled = true
  previousResponseApi = window.responseApi

  window.responseApi = function responseApi (payload) {
    if (!nativeActive) {
      logWorldCupDebug('native api orphan response', {
        payloadType: typeof payload
      })
      if (typeof previousResponseApi === 'function') {
        previousResponseApi(payload)
      }
      return
    }

    const active = nativeActive
    nativeActive = null
    window.clearTimeout(active.timer)

    let data = payload
    try {
      if (typeof payload === 'string') {
        data = JSON.parse(payload)
      }
      logWorldCupDebug('native api response', {
        url: active.url,
        error_code: data && data.error_code,
        reason: data && data.reason,
        dataLength: readResultLength(data)
      })
      active.resolve(data || {})
    } catch (error) {
      logWorldCupDebug('native api parse error', {
        message: error.message,
        payload: String(payload).slice(0, 300)
      })
      active.reject(error)
    } finally {
      runNativeQueue()
    }
  }
}

function runNativeQueue () {
  if (nativeActive || nativeQueue.length === 0) {
    return
  }

  const active = nativeQueue.shift()
  nativeActive = active

  const timer = window.setTimeout(() => {
    if (nativeActive !== active) {
      return
    }
    nativeActive = null
    const error = new Error('Native request timeout')
    logWorldCupDebug('native api timeout', {
      url: active.url,
      params: active.params
    })
    active.reject(error)
    runNativeQueue()
  }, REQUEST_TIMEOUT)

  active.timer = timer

  const message = {
    url: active.url,
    method: active.method,
    parameter: active.params,
    header: {}
  }

  logWorldCupDebug('native api request', message)
  window.webkit.messageHandlers.requestApi.postMessage(message)
}

function requestByNative (config) {
  installNativeCallback()
  const nextConfig = appendTimestamp(config)
  const method = String(nextConfig.method || 'get').toLowerCase()
  const params = method === 'get'
    ? (nextConfig.params || {})
    : (nextConfig.data || nextConfig.params || {})
  const url = buildNativeUrl(nextConfig.url)

  return new Promise((resolve, reject) => {
    nativeQueue.push({
      url,
      method,
      params,
      resolve,
      reject
    })
    runNativeQueue()
  })
}

function requestSafeNewsInfoByNative (config) {
  const safeConfig = Object.assign({}, config, {
    url: '/worldcup2026/newsinfo_safe'
  })
  return requestByNative(safeConfig).then(resp => {
    const result = resp && resp.result ? resp.result : {}
    if (result.encoding !== 'base64-json' || !result.data) {
      return resp
    }
    try {
      const decoded = JSON.parse(decodeBase64Utf8(result.data))
      logWorldCupDebug('native safe api decoded', {
        error_code: decoded && decoded.error_code,
        reason: decoded && decoded.reason
      })
      return decoded
    } catch (error) {
      logWorldCupDebug('native safe api decode error', {
        message: error.message
      })
      return Promise.reject(error)
    }
  })
}

function decodeBase64Utf8 (value) {
  const binary = window.atob(value)
  if (window.TextDecoder) {
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return new window.TextDecoder('utf-8').decode(bytes)
  }
  return decodeURIComponent(escape(binary))
}

service.interceptors.request.use(config => {
  config = appendTimestamp(config)
  logWorldCupDebug('api request', {
    method: config.method,
    baseURL: config.baseURL,
    url: config.url,
    params: config.params
  })
  return config
})

service.interceptors.response.use(
  response => {
    const data = response.data || {}
    const result = data.result || {}
    logWorldCupDebug('api response', {
      url: response.config && response.config.url,
      status: response.status,
      error_code: data.error_code,
      reason: data.reason,
      dataLength: Array.isArray(result.data) ? result.data.length : undefined
    })
    return data
  },
  error => {
    logWorldCupDebug('api error', {
      message: error.message,
      code: error.code,
      url: error.config && error.config.url,
      baseURL: error.config && error.config.baseURL,
      params: error.config && error.config.params,
      status: error.response && error.response.status,
      response: error.response && error.response.data
    })
    return Promise.reject(error)
  }
)

export default function worldcupRequest (config) {
  if (shouldUseSafeNative(config)) {
    return requestSafeNewsInfoByNative(config)
  }
  if (shouldUseNativeApi()) {
    return requestByNative(config)
  }
  return service(config)
}
